import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';

import { combineLatest, filter, map, Observable, catchError, of } from 'rxjs';

import { getModelTypeDisplayName } from '@live/live.utils';
import { ILiveCameraTab } from '@live/pages/live-camera-page/interfaces';
import { MediaFile } from '@live/reducers/live-media-files.reducer';
import { PresetParams } from '@live/pages/live-ai-models-and-presets-page/tabs/live-presets-tab/components/preset-details-wrapper/interfaces';
import { IPresetDetails } from '@live/pages/live-ai-models-and-presets-page/tabs/live-presets-tab/components/apply-preset/interfaces';
import { LiveGalleryTab } from '@live/pages/live-gallery-page/interfaces';
import { IUILiveInsightType, PresetsTab } from '@live/pages/live-ai-models-and-presets-page/interfaces';
import { MediaFilesViewType } from '@live/reducers/live-media-files-view.reducer';
import {
  IDrawingTool,
  DrawingToolType
} from '@live/pages/live-camera-page/components/live-drawing-control-bar/components/live-drawing-tools-selector';
import * as EdgeActions from '@web/core/actions';
import { IUISpatialAnalysisRule } from '@live/pages/live-camera-page/components/camera-insights/components/spatial-analysis-tab/interfaces';
import { FilterKeys } from '@live/components/live-filters/interfaces';

import { IAction } from '@common/modules/shared/interfaces';

import { Camera } from './../reducers/live-cameras.reducer';
import { PinnedCamera } from './../reducers/live-pinned-cameras.reducer';
import * as liveState from '../reducers';
import * as presetsActions from '../actions/live-presets.actions';
import * as builtInInsightsActions from '../actions/live-builtIn-insights.actions';
import * as cameraActions from '../actions/live-cameras.actions';
import * as liveGalleryActions from '../actions/live-gallery.actions';
import * as LiveMediaFilesActions from '../actions/live-media-files.actions';
import * as customInsightsActions from '../actions/live-custom-insights.actions';
import * as spatialActions from '../actions/live-spatial.actions';
import * as SegmentsActions from '../actions/live-segments.actions';
import * as fromBuiltInInsights from '../selectors/live-builtIn-insights.selectors';
import * as fromPresets from '../selectors/live-presets.selectors';
import * as fromLiveMediaFiles from '../selectors/live-media-files.selectors';
import * as fromSegments from '../selectors/live-segments.selectors';
import * as fromLiveMediaFilesView from '../selectors/live-media-files-view.selectors';
import * as fromLiveEdge from '../selectors/live-edge.selectors';
import * as fromLiveGallery from '../selectors/live-gallery.selectors';
import * as fromSpatial from '../selectors/live-spatial.selectors';
import * as fromCameras from '../selectors/live-cameras.selectors';
import * as fromCameraView from '../selectors/live-camera-view.selector';
import * as fromCustomInsights from '../selectors/live-custom-insights.selectors';
import { LiveModelCatalogService } from './live-model-catalog/live-model-catalog.service';

import ICameraUpdate = Microsoft.VideoIndexer.Live.Contracts.ICameraUpdate;
import ICamera = Microsoft.VideoIndexer.Live.Contracts.ICamera;
import ICustomInsight = Microsoft.VideoIndexer.Live.Contracts.ICustomInsight;
import INextPage = Microsoft.VideoIndexer.Contracts.INextPage;
import ILiveMediaFile = Microsoft.VideoIndexer.Live.Contracts.ILiveMediaFile;
import ILiveSegment = Microsoft.VideoIndexer.Live.Contracts.ILiveSegment;
import ISpatialAnalysisRule = Microsoft.VideoIndexer.Live.Contracts.ISpatialAnalysisRule;
import ILiveMonitoredZone = Microsoft.VideoIndexer.Live.Contracts.ILiveMonitoredZone;
import ILiveSegmentsResults = Microsoft.VideoIndexer.Live.Contracts.ILiveSegmentsResults;

// eslint-disable-next-line max-len
import type { ICreateCustomInsightDialogData } from '@live/pages/live-ai-models-and-presets-page/tabs/live-custom-insights-tab/components/live-custom-insight-dialog/interfaces';
// eslint-disable-next-line max-len
import type { ICreateCustomModelDialogData } from '@live/pages/live-ai-models-and-presets-page/tabs/live-custom-insights-tab/components/live-custom-model-dialog/interfaces';

@Injectable({
  providedIn: 'root'
})
export class LiveStoreService {
  constructor(private readonly store: Store<liveState.IState>, private liveModelCatalogService: LiveModelCatalogService) {}

  public get extensionDetails$() {
    return this.store.select(fromLiveEdge.getExtensionDetailsDialogData);
  }

  // When media files upload is disabled - we still showing up the media files tab
  // But it will have the camera's saved recording files only.
  public get isMediaFilesUploadEnabled$(): Observable<boolean> {
    return this.extensionDetails$.pipe(map(details => details?.mediaUploadsEnabled ?? false));
  }

  public get isSearchWithRAGEnabled$(): Observable<boolean> {
    return this.extensionDetails$.pipe(map(details => details?.searchWithRAGEnabled ?? false));
  }

  public get isLiveGalleryRoute$(): Observable<boolean> {
    return this.store.select(fromLiveEdge.isLiveGalleryRoute);
  }

  public get selectCurrentExtensionName$(): Observable<string> {
    return this.store.select(fromLiveEdge.getExtensionName);
  }

  // ==============================
  // Built In Insights
  // ==============================

  public get selectBuiltInInsights$(): Observable<IUILiveInsightType[]> {
    return this.store.select(fromBuiltInInsights.selectBuiltInInsights).pipe(
      map(insights =>
        insights.map(insight => {
          const insightType = `${insight.insightName}-${insight.modelType}`;
          const insightCatalogItem = this.liveModelCatalogService.getInsightById(insight.id);
          return {
            ...insight,
            insightType,
            modelTypeDisplayName: getModelTypeDisplayName(insight.modelType),
            description: insightCatalogItem?.description || '--',
            isNew: insightCatalogItem?.new,
            isUpdated: insightCatalogItem?.isUpdated,
            lastUpdateTime: insightCatalogItem?.updatedAt,
            createTime: '',
            prompt: null,
            // @TODO: remove this
            insightId: insight.id
          };
        })
      )
    );
  }

  public get isBuiltInInsightsLoaded$(): Observable<boolean> {
    return this.store.select(fromBuiltInInsights.isBuiltInInsightsLoaded);
  }

  public get isLoadBuiltInInsightsError$(): Observable<boolean> {
    return this.store.select(fromBuiltInInsights.hasError);
  }

  public loadBuiltInInsights() {
    this.store.dispatch(builtInInsightsActions.loadBuiltInInsights({}));
  }

  // ==============================
  // AI Presets
  // ==============================

  // getters

  public get selectPresets$() {
    return this.store.select(fromPresets.selectPresets);
  }

  public get getPresetError$() {
    return this.store.select(fromPresets.getPresetError);
  }

  public selectIsPresetLoaded(id: string) {
    return this.store.select(fromPresets.selectIsPresetLoaded(id));
  }

  public selectPreset(id: string) {
    return this.store.select(fromPresets.selectPreset(id));
  }

  public selectPresetName(id: string) {
    return this.store.select(fromPresets.selectPresetName(id));
  }

  public selectPresetIncludedInsights(id: string): Observable<IUILiveInsightType[]> {
    return this.store.select(fromPresets.selectPresetInsightTypes(id)).pipe(
      map(insights =>
        insights.map(insight => {
          const insightCatalogItem = this.liveModelCatalogService.getInsightById(insight.id);
          return {
            ...insight,
            modelTypeDisplayName: getModelTypeDisplayName(insight.modelType),
            insightType: `${insight.insightName}-${insight.modelType}`,
            description: insightCatalogItem?.description || '--',
            isNew: insightCatalogItem?.new,
            isUpdated: insightCatalogItem?.isUpdated,
            lastUpdateTime: insightCatalogItem?.updatedAt,
            createTime: '',
            prompt: null,
            // @TODO: remove this
            insightId: insight.id
          };
        })
      )
    );
  }

  public resetCameras(): void {
    this.clearCameras();
    this.clearPinnedCameras();
  }

  public clearCameras(): void {
    this.store.dispatch(cameraActions.clearCameras());
  }

  public clearPinnedCameras(): void {
    this.store.dispatch(cameraActions.clearPinnedCameras());
  }

  public openCreateCustomInsightDialog() {
    this.store.dispatch(customInsightsActions.openCreateCustomInsightDialog());
  }

  public openEditCustomInsightDialog(insight: ICustomInsight) {
    this.store.dispatch(customInsightsActions.openEditCustomInsightDialog({ insight }));
  }

  public openDuplicateCustomInsightDialog(insight: ICustomInsight) {
    this.store.dispatch(customInsightsActions.openDuplicateCustomInsightDialog({ insight }));
  }

  public selectAffectedCamerasCount(id: string): Observable<number> {
    return this.store.select(fromPresets.selectAffectedCamerasCount(id));
  }

  public selectAffectedCamerasStatus(id: string) {
    return this.store.select(fromPresets.selectAffectedCamerasStatus(id));
  }

  public getPresetData(presetId: string) {
    return combineLatest([
      this.selectIsPresetLoaded(presetId),
      this.selectPresetName(presetId),
      this.selectAffectedCamerasStatus(presetId),
      this.selectAffectedCamerasCount(presetId),
      this.selectPresetIncludedInsights(presetId),
      this.isAllInsightsLoaded$
    ]).pipe(
      // filter observables until full data is loaded
      filter(([_, __, ___, affectedCamerasCount, ...rest]) => {
        return affectedCamerasCount !== undefined;
      }),
      map(([isPresetLoaded, presetName, affectedCamerasStatus, affectedCamerasCount, presetIncludedInsights, isAllInsightsLoaded]) => ({
        isPresetLoaded,
        presetName,
        affectedCamerasStatus,
        affectedCamerasCount,
        presetIncludedInsights,
        isAllInsightsLoaded
      }))
    );
  }

  public get selectPresetsDetailsList$(): Observable<IPresetDetails[]> {
    return this.store.select(fromPresets.getPresetsDetails);
  }

  public get isPresetsLoaded$() {
    return this.store.select(fromPresets.selectIsPresetsLoaded);
  }

  public get isSavingPreset$() {
    return this.store.select(fromPresets.selectIsSaving);
  }

  public get selectedPresetsPageTab$() {
    return this.store.select(fromPresets.selectPresetsPageTab);
  }

  public get getLiveGallerySelectedTab$() {
    return this.store.select(fromLiveGallery.getLiveGallerySelectedTab);
  }

  public get selectCameras$() {
    return this.store.select(fromCameras.selectCameras);
  }

  public get selectCamerasNextPage$() {
    return this.store.select(fromCameras.selectCamerasNextPage);
  }

  public get selectIsCamerasListLoaded$() {
    return this.store.select(fromCameras.selectIsCamerasListLoaded);
  }

  public get selectIsCamerasListLoading$(): Observable<boolean> {
    return this.store.select(fromCameras.selectsCamerasListLoading);
  }

  public get selectHasCamerasListError$(): Observable<boolean> {
    return this.store.select(fromCameras.selectHasCamerasListError);
  }

  public get selectIsPinnedCamerasListLoaded$(): Observable<boolean> {
    return this.store.select(fromCameras.selectIsPinnedCamerasListLoaded);
  }

  public get selectIsPinnedCamerasListLoading$(): Observable<boolean> {
    return this.store.select(fromCameras.selectIsPinnedCamerasListLoading);
  }

  public get selectPinnedCameras$(): Observable<PinnedCamera[]> {
    return this.store.select(fromCameras.selectPinnedCameras);
  }

  public get selectPinnedCamerasNextPage$() {
    return this.store.select(fromCameras.selectPinnedCamerasNextPage);
  }

  public get getPinnedCameras$() {
    return this.store.select(fromCameras.selectPinnedCameras);
  }

  public getCameraById(cameraId: string): Observable<Camera> {
    return this.store.select(fromCameras.selectCameraById(cameraId));
  }

  public getCameraById$(cameraId: string): Observable<Camera> {
    return this.store.select(fromCameras.selectCameraById(cameraId));
  }

  public get selectedCamera$(): Observable<Camera> {
    return this.store.select(fromCameras.getSelectedCamera);
  }

  public get selectCameraAgentJob$(): Observable<Microsoft.VideoIndexer.Live.Contracts.ILiveCameraAgentJob> {
    return this.store.select(fromCameras.selectCameraAgentJob);
  }

  public get cameraTab$(): Observable<ILiveCameraTab> {
    return this.store.select(fromCameraView.getCameraTab);
  }

  // actions
  public viewCamerasByPresetId(presetId: string, openInNewTab: boolean = false) {
    this.store.dispatch(presetsActions.viewCamerasClicked({ presetId, openInNewTab }));
  }

  public cameraTabChanged(tab: ILiveCameraTab) {
    this.store.dispatch(cameraActions.cameraTabChanged({ tab }));
  }

  public viewAiInsights() {
    this.store.dispatch(presetsActions.viewAiInsightsClicked());
  }

  public loadPresetsWithAffectedCamerasCount() {
    this.store.dispatch(presetsActions.loadPresetsWithAffectedCamerasCount());
  }

  public loadPreset(presetId: string) {
    this.store.dispatch(presetsActions.getPreset({ presetId }));
  }

  public createPreset(preset: PresetParams) {
    this.store.dispatch(presetsActions.createPreset({ preset }));
  }

  public updatePreset(preset: PresetParams) {
    this.store.dispatch(presetsActions.updatePreset({ presetId: preset.id, data: { name: preset.name, insightTypes: preset.insightTypes } }));
  }

  public openCreatePresetDialog(presetId: string = '', preselectedInsights: IUILiveInsightType[] = []) {
    this.store.dispatch(presetsActions.openCreatePresetDialog({ presetId, preselectedInsights }));
  }

  public openEditPresetDialog(presetId: string) {
    this.store.dispatch(presetsActions.openEditPresetDialog({ presetId }));
  }

  public liveGalleryTabChanged(tab: LiveGalleryTab) {
    this.store.dispatch(liveGalleryActions.tabChanged({ tab }));
  }

  public manageAiInsightsClicked() {
    this.store.dispatch(cameraActions.manageAiInsightsClicked());
  }

  public openApplyPresetDialog(cameras: Microsoft.VideoIndexer.Live.Contracts.ICamera[]) {
    this.store.dispatch(presetsActions.openApplyPresetDialog({ cameras }));
  }

  public applyPreset(presetId: string, cameraIds: ICamera['id'][]) {
    this.store.dispatch(presetsActions.applyPreset({ presetId, cameraIds }));
  }

  public managePresets() {
    this.store.dispatch(presetsActions.managePresetsClicked());
  }

  public loadPresets() {
    this.store.dispatch(presetsActions.loadPresetsWithAffectedCamerasCount());
  }

  public loadPresetsNextPage() {
    this.store.dispatch(presetsActions.loadPresetsNextPage());
  }

  public loadPresetsWithAffectedCamerasCountNextPage() {
    this.store.dispatch(presetsActions.loadPresetsWithAffectedCamerasCountNextPage());
  }

  public manageCamerasClicked() {
    this.store.dispatch(cameraActions.manageCamerasClicked());
  }

  public goToCameraPage(cameraId: string) {
    this.store.dispatch(cameraActions.goToCameraPage({ cameraId }));
  }

  public listCameras() {
    this.store.dispatch(cameraActions.listCameras());
  }

  public listPinnedCameras({ pageSize, skip }: { pageSize?: number; skip?: number } = {}) {
    this.store.dispatch(cameraActions.listPinnedCameras({ pageSize, skip }));
  }

  public presetsTabChanged(tab: PresetsTab) {
    this.store.dispatch(presetsActions.presetsTabChanged({ tab }));
  }

  public viewCameraSettingsClicked(cameraIds: ICamera['id'][]) {
    this.store.dispatch(cameraActions.viewCameraSettingsClicked({ cameraIds }));
  }

  public viewCameraPropertiesClicked(cameraId: ICamera['id']) {
    this.store.dispatch(cameraActions.viewCameraPropertiesClicked({ cameraId }));
  }

  public unpinCamera(camera: ICamera) {
    this.store.dispatch(cameraActions.unpinCamera({ camera }));
  }

  public pinCamera(camera: ICamera) {
    this.store.dispatch(cameraActions.pinCamera({ camera }));
  }

  public updateCamera(updatedCameras: ICameraUpdate[]) {
    this.store.dispatch(cameraActions.updateCamera({ updatedCameras }));
  }

  public selectAreCamerasUpdating$(cameraIds: string[]): Observable<boolean> {
    return this.store.select(fromCameras.selectAreCamerasUpdating(cameraIds));
  }

  public openDeletePresetDialog(presets: IPresetDetails[]) {
    this.store.dispatch(presetsActions.openDeletePresetDialog({ presets }));
  }

  public goToAioPortalAction() {
    this.store.dispatch(cameraActions.goToAioPortalAction());
  }

  public goToCameraManagementAction() {
    this.store.dispatch(cameraActions.manageCamerasClicked());
  }

  public selectExtension(extensionId: string) {
    this.store.dispatch(EdgeActions.selectEdgeExtension({ id: extensionId }));
  }

  // ==============================
  // Custom Insights
  // ==============================
  public get selectCustomInsights$(): Observable<IUILiveInsightType[]> {
    return this.store.select(fromCustomInsights.getCustomInsights);
  }

  public getCustomInsightById$(insightId: string): Observable<ICustomInsight> {
    return this.store.select(state => {
      const selector = fromCustomInsights.getCustomInsightById(insightId);
      return selector(fromCustomInsights.getCustomInsightsState(state));
    });
  }

  public get isCustomInsightsLoaded$(): Observable<boolean> {
    return this.store.select(fromCustomInsights.isCustomInsightsLoaded);
  }

  public get isCustomInsightsReady$(): Observable<boolean> {
    return combineLatest([this.isCustomInsightsLoaded$, this.customInsightsError$]).pipe(
      map(([isCustomInsightsLoaded, customInsightsError]) => {
        return isCustomInsightsLoaded || !!customInsightsError;
      })
    );
  }

  public get selectAllInsights$(): Observable<IUILiveInsightType[]> {
    return combineLatest([this.selectCustomInsights$, this.selectBuiltInInsights$]).pipe(
      map(([customInsights, builtInModels]) => {
        return [...customInsights, ...builtInModels];
      })
    );
  }

  public get isAllInsightsLoaded$(): Observable<boolean> {
    return combineLatest([this.isCustomInsightsReady$, this.isBuiltInInsightsLoaded$]).pipe(
      map(([isCustomInsightsLoaded, isBuiltInInsightsLoaded]) => {
        return isCustomInsightsLoaded && isBuiltInInsightsLoaded;
      })
    );
  }

  public get isInsightsError$(): Observable<boolean> {
    return combineLatest([this.isLoadBuiltInInsightsError$, this.customInsightsError$]).pipe(
      map(([isLoadBuiltInInsightsError, customInsightsError]) => {
        return isLoadBuiltInInsightsError || !!customInsightsError;
      })
    );
  }

  public get isCustomInsightsLoading$(): Observable<boolean> {
    return this.store.select(fromCustomInsights.isCustomInsightsLoading);
  }

  public get isCustomInsightsCreating$(): Observable<boolean> {
    return this.store.select(fromCustomInsights.isCustomInsightsCreating);
  }

  public get isCustomInsightsDeleting$(): Observable<boolean> {
    return this.store.select(fromCustomInsights.isCustomInsightsDeleting);
  }

  public get customInsightsError$(): Observable<string> {
    return this.store.select(fromCustomInsights.getCustomInsightsError);
  }

  public get isCustomInsightsUploading$(): Observable<boolean> {
    return this.store.select(fromCustomInsights.isCustomInsightsUploading).pipe(catchError(() => of(false)));
  }

  public get getLastCreatedInsightId$(): Observable<string> {
    return this.store.select(fromCustomInsights.getLastCreatedInsightId);
  }

  public get temporaryImageFile$(): Observable<File | null> {
    return this.store.select(fromCustomInsights.getTemporaryImageFile);
  }

  public get customInsightImageData$(): Observable<{ [key: string]: string }> {
    return this.store.select(fromCustomInsights.getCustomInsightImageData);
  }

  public get isCustomInsightsImageLoading$(): Observable<boolean> {
    return this.store.select(fromCustomInsights.isCustomInsightsImageLoading);
  }

  public setTemporaryImageFile(file: File): void {
    this.store.dispatch(customInsightsActions.setTemporaryImageFile({ file }));
  }

  public clearTemporaryImageFile(): void {
    this.store.dispatch(customInsightsActions.clearTemporaryImageFile());
  }

  public loadAllInsights() {
    this.store.dispatch(customInsightsActions.loadAllInsights());
  }

  public listCustomInsights(): void {
    this.store.dispatch(customInsightsActions.listCustomInsights());
  }

  public createCustomInsight(insightData: ICreateCustomInsightDialogData): void {
    this.store.dispatch(customInsightsActions.createCustomInsight({ insightData }));
  }

  public updateCustomInsight(insightId: string, insightData: ICreateCustomInsightDialogData): void {
    this.store.dispatch(customInsightsActions.updateCustomInsight({ insightId, insightData }));
  }

  public uploadCustomInsightImage(insightId: string, formData: FormData, imageName: string): void {
    this.store.dispatch(customInsightsActions.uploadCustomInsightImage({ insightId, formData, imageName }));
  }

  public getCustomInsightImage(insightId: string, imageId: string): void {
    this.store.dispatch(customInsightsActions.getCustomInsightImage({ insightId, imageId }));
  }

  public deleteCustomInsightImage(insightId: string, imageId: string): void {
    this.store.dispatch(customInsightsActions.deleteCustomInsightImage({ insightId, imageId }));
  }

  public deleteCustomInsights(insightIds: string[]): void {
    this.store.dispatch(customInsightsActions.deleteCustomInsights({ insightIds }));
  }

  public openDeleteCustomInsightDialog(insights: ICustomInsight[]): void {
    this.store.dispatch(customInsightsActions.openDeleteCustomInsightDialog({ insights }));
  }

  /**
   * Handles the process of changing an image for a custom insight
   * This includes deleting the old image (if any) and uploading a new one (if any)
   * @param insightId The ID of the insight being edited
   * @param oldImageId The ID of the image to be deleted (optional)
   * @param newImage The new image file to upload (optional)
   * @param insightData The updated insight data
   */
  public handleInsightImageChange(insightId: string, oldImageId?: string, newImage?: File, insightData?: ICreateCustomInsightDialogData): void {
    this.store.dispatch(
      customInsightsActions.handleInsightImageChange({
        insightId,
        oldImageId,
        newImage,
        insightData
      })
    );
  }

  public switchInsightTypeToImage(insightId: string, newImage: File, insightData: ICreateCustomInsightDialogData): void {
    this.store.dispatch(
      customInsightsActions.switchInsightTypeToImage({
        insightId,
        newImage,
        insightData
      })
    );
  }

  // ==============================
  // Custom Models
  // ==============================
  public openCreateCustomModelDialog(): void {
    this.store.dispatch(customInsightsActions.openCreateCustomModelDialog());
  }

  public openEditCustomModelDialog(model: ICustomInsight): void {
    this.store.dispatch(customInsightsActions.openEditCustomModelDialog({ model }));
  }

  public openDuplicateCustomModelDialog(model: ICustomInsight): void {
    this.store.dispatch(customInsightsActions.openDuplicateCustomModelDialog({ model }));
  }

  public createCustomModel(modelData: ICreateCustomModelDialogData, formData?: FormData): void {
    this.store.dispatch(customInsightsActions.createCustomModel({ modelData, formData }));
  }

  public updateCustomModel(
    id: string,
    modelData: ICreateCustomModelDialogData & {
      deletedPositiveImageIds?: string[];
      deletedNegativeImageIds?: string[];
    },
    formData?: FormData
  ): void {
    this.store.dispatch(customInsightsActions.updateCustomModel({ modelId: id, modelData, formData }));
  }

  // Live player
  public listAllCameraMediaFiles(cameraId: string): void {
    this.store.dispatch(LiveMediaFilesActions.listAllCameraMediaFiles({ cameraId }));
  }

  public get selectCurrentCameraAllMediaFiles$(): Observable<MediaFile[]> {
    return this.store.select(fromLiveMediaFiles.selectCurrentCameraAllMediaFiles);
  }

  public get selectCurrentCameraAllMediaFilesIsLoading$(): Observable<boolean> {
    return this.store.select(fromLiveMediaFiles.selectCurrentCameraAllMediaFilesIsLoading);
  }

  public get selectCurrentCameraAllMediaFilesIsLoaded$(): Observable<boolean> {
    return this.store.select(fromLiveMediaFiles.selectCurrentCameraAllMediaFilesIsLoaded);
  }

  public get selectCurrentCameraAllMediaFilesHasError$(): Observable<boolean> {
    return this.store.select(fromLiveMediaFiles.selectCurrentCameraAllMediaFilesHasError);
  }

  // Live Media Files
  public listMediaFiles(): void {
    this.store.dispatch(LiveMediaFilesActions.listMediaFiles());
  }

  public loadMediaFilesNextPage(): void {
    this.store.dispatch(LiveMediaFilesActions.loadMediaFilesNextPage());
  }

  public clearMediaFiles(): void {
    this.store.dispatch(LiveMediaFilesActions.clearMediaFiles());
  }

  public goToMediaFilePage(mediaFileId: string, cameraId?: string, time?: number): void {
    this.store.dispatch(LiveMediaFilesActions.goToMediaFilePage({ mediaFileId, cameraId, time }));
  }

  public openMediaFileReIndexDialog(videoId: string): void {
    this.store.dispatch(LiveMediaFilesActions.openMediaFileReIndexDialog({ videoId }));
  }

  public get selectMediaFiles$(): Observable<MediaFile[]> {
    return this.store.select(fromLiveMediaFiles.selectMediaFilesList);
  }

  public get selectMediaFilesListNextPage$(): Observable<INextPage> {
    return this.store.select(fromLiveMediaFiles.selectMediaFilesListNextPage);
  }

  public get selectIsMediaFilesLoading$(): Observable<boolean> {
    return this.store.select(fromLiveMediaFiles.selectsMediaFilesListLoading);
  }

  public get selectIsMediaFilesLoaded$(): Observable<boolean> {
    return this.store.select(fromLiveMediaFiles.selectIsMediaFilesListLoaded);
  }

  public get hasVideosError$(): Observable<boolean> {
    return this.store.select(fromLiveMediaFiles.selectHasMediaFilesListError);
  }

  public selectMediaFileById$(mediaFileId: string): Observable<MediaFile> {
    return this.store.select(fromLiveMediaFiles.selectMediaFileById(mediaFileId));
  }

  public selectIsInsightsTypesLoaded$(mediaFileId: string): Observable<boolean> {
    return this.store.select(fromLiveMediaFiles.selectIsInsightsTypesLoaded(mediaFileId));
  }

  public deleteMediaFile(mediaFileId: string): void {
    this.store.dispatch(LiveMediaFilesActions.deleteMediaFile({ mediaFileId }));
  }

  public openDeleteMediaFileDialog(mediaFile: ILiveMediaFile): void {
    this.store.dispatch(LiveMediaFilesActions.openDeleteMediaFileDialog({ mediaFile }));
  }

  public get isMediaFileDeleting$(): Observable<boolean> {
    return this.store.select(fromLiveMediaFiles.selectIsMediaFileDeleting);
  }

  // Video Tags
  public loadVideoTags(mediaFileId: string): void {
    this.store.dispatch(LiveMediaFilesActions.loadVideoTags({ mediaFileId }));
  }

  public updateVideoTags(mediaFileId: string, desiredTagValues: string[]): void {
    this.store.dispatch(LiveMediaFilesActions.updateVideoTags({ mediaFileId, desiredTagValues }));
  }

  public selectVideoTags$(mediaFileId: string): Observable<{ id: string; key: string; value: string }[] | undefined> {
    return this.store.select(fromLiveMediaFiles.selectVideoTags(mediaFileId));
  }

  public selectAreVideoTagsLoading$(mediaFileId: string): Observable<boolean | undefined> {
    return this.store.select(fromLiveMediaFiles.selectAreVideoTagsLoading(mediaFileId));
  }

  public selectAreVideoTagsUpdating$(mediaFileId: string): Observable<boolean | undefined> {
    return this.store.select(fromLiveMediaFiles.selectAreVideoTagsUpdating(mediaFileId));
  }

  public selectAreVideoTagsDisabled$(mediaFileId: string): Observable<boolean> {
    return combineLatest([this.selectAreVideoTagsLoading$(mediaFileId), this.selectAreVideoTagsUpdating$(mediaFileId)]).pipe(
      map(([isLoading, isUpdating]) => !!isLoading || !!isUpdating)
    );
  }

  public get selectMediaFilesViewType$(): Observable<MediaFilesViewType> {
    return this.store.select(fromLiveMediaFilesView.selectMediaFilesViewType);
  }

  public get selectSegmentsLoading$(): Observable<boolean> {
    return this.store.select(fromSegments.selectSegmentsLoading);
  }

  public get selectSegmentsError$(): Observable<string | null> {
    return this.store.select(fromSegments.selectSegmentsError);
  }

  public get selectSegmentLoaded$(): Observable<boolean> {
    return this.store.select(fromSegments.selectSegmentsLoaded);
  }

  public get selectSegmentTotal$(): Observable<number> {
    return this.store.select(fromSegments.selectSegmentTotal);
  }

  public get selectSegments$(): Observable<ILiveSegment[]> {
    return this.store.select(fromSegments.selectAllSegments);
  }

  public get selectSegmentsSearchContext$(): Observable<ILiveSegmentsResults['searchContext']> {
    return this.store.select(fromSegments.selectSegmentsSearchContext);
  }

  public searchSegments(query: string, text: string, filters: { [key in FilterKeys]?: string[] }): void {
    this.store.dispatch(SegmentsActions.searchStarted({ query, text, filters }));
  }

  public clearSegmentsSearch(): void {
    this.store.dispatch(SegmentsActions.clearSearch());
  }

  public renameMediaFile(mediaFileId: string, newName: string, startTime: string): void {
    this.store.dispatch(LiveMediaFilesActions.renameMediaFile({ mediaFileId, newName, startTime }));
  }

  public openRenameMediaFileDialog(mediaFile: ILiveMediaFile): void {
    this.store.dispatch(LiveMediaFilesActions.openRenameMediaFileDialog({ mediaFile }));
  }

  public get isMediaFileRenaming$(): Observable<boolean> {
    return this.store.select(fromLiveMediaFiles.selectIsMediaFileRenaming);
  }

  // ==============================
  // Spatial Analysis
  // ==============================

  public get selectCurrentDrawingTool$(): Observable<IDrawingTool | null> {
    return this.store.select(fromSpatial.selectCurrentDrawingTool);
  }

  public get selectIsSpatialTabEditMode$(): Observable<boolean> {
    return this.store.select(fromSpatial.selectIsEditMode);
  }

  public get selectIsDrawingState$(): Observable<boolean> {
    return combineLatest([this.store.select(fromSpatial.selectIsDrawing), this.store.select(fromSpatial.selectHasSelectedTool)]).pipe(
      map(([isDrawing, currentDrawingTool]) => isDrawing || !!currentDrawingTool)
    );
  }

  public get selectSpatialRules$() {
    return this.store.select(fromSpatial.selectAllEntities);
  }

  public get isSpatialRulesLoaded$() {
    return this.store.select(fromSpatial.selectIsLoaded);
  }

  public get isSpatialRulesLoadedError$() {
    return this.store.select(fromSpatial.selectIsError);
  }

  public get selectSpatialRulesCount$(): Observable<number> {
    return combineLatest([this.store.select(fromSpatial.selectAllEntities), this.store.select(fromSpatial.selectInProgressShape)]).pipe(
      map(([entities, inProgressShape]) => Object.keys(entities).length + (inProgressShape ? 1 : 0))
    );
  }

  public get selectMonitoredZones$(): Observable<ILiveMonitoredZone[]> {
    return this.store.select(fromSpatial.selectMonitoredZones);
  }

  // New proper spatial methods using spatialActions
  public selectDrawingTool(tool: IDrawingTool): void {
    this.store.dispatch(spatialActions.selectDrawingTool({ tool }));
  }

  public get selectCounterFilters$(): Observable<IAction[]> {
    return this.store.select(fromSpatial.selectCounterFilters);
  }

  public get selectBoxFilters$(): Observable<IAction[]> {
    return this.store.select(fromSpatial.selectBoxFilters);
  }

  public clearSelectedTool(): void {
    this.store.dispatch(spatialActions.clearSelectedTool());
  }

  public enterSpatialEditMode(toolType?: DrawingToolType): void {
    this.store.dispatch(spatialActions.enterEditMode({ toolType }));
  }

  public exitSpatialEditMode(resetInProgressShape: boolean = false): void {
    this.store.dispatch(spatialActions.exitEditMode({ resetInProgressShape }));
  }

  public completeShape(shapeData: ISpatialAnalysisRule): void {
    this.store.dispatch(spatialActions.completeShape({ shapeData }));
  }

  public updateInProgressShape(shapeData: IUISpatialAnalysisRule): void {
    this.store.dispatch(spatialActions.updateInProgressShape({ shapeData }));
  }

  public markInProgressShapeAsUpdating(): void {
    this.store.dispatch(spatialActions.markInProgressShapeAsUpdating());
  }

  public startDrawing(): void {
    this.store.dispatch(spatialActions.startDrawing());
  }

  public stopDrawing(): void {
    this.store.dispatch(spatialActions.stopDrawing());
  }

  public get selectInProgressShape$(): Observable<ISpatialAnalysisRule | null> {
    return this.store.select(fromSpatial.selectInProgressShape);
  }

  public get selectSpatialRuleCurrentEditableRuleId$(): Observable<string> {
    return this.store.select(fromSpatial.selectCurrentEditableRuleId);
  }

  public get selectCompletedShapesCount$(): Observable<number> {
    return this.store.select(fromSpatial.selectCompletedShapesCount);
  }

  public get selectAreToolsAvailable$(): Observable<boolean> {
    return this.store.select(fromSpatial.selectAreToolsAvailable);
  }

  public updateSpatialRuleCurrentEditableRuleId(ruleId: string): void {
    this.store.dispatch(spatialActions.updateCurrentEditableRuleId({ ruleId }));
  }

  public selectPlayerFilters(counters: IAction[], boxes: IAction[]) {
    this.store.dispatch(spatialActions.selectPlayerFilters({ counters, boxes }));
  }

  public togglePlayerCounters(counterValues: string[]) {
    this.store.dispatch(spatialActions.togglePlayerCounters({ counterValues }));
  }

  public togglePlayerBoxes(boxValues: string[]) {
    this.store.dispatch(spatialActions.togglePlayerBoxes({ boxValues }));
  }

  public toggleSpatialInsights(excludedInsights: string[]) {
    this.store.dispatch(spatialActions.toggleSpatialInsights({ excludedInsights }));
  }

  public createSpatialAnalysisRule(rule: ISpatialAnalysisRule): void {
    this.store.dispatch(spatialActions.createSpatialAnalysisRule({ rule }));
  }

  public updateSpatialAnalysisRule(id: string, rule: ISpatialAnalysisRule): void {
    this.store.dispatch(spatialActions.updateSpatialAnalysisRule({ id, rule }));
  }

  public deleteSpatialAnalysisRule(ruleId: string): void {
    this.store.dispatch(spatialActions.deleteSpatialAnalysisRule({ ruleId }));
  }

  public listSpatialAnalysisRules(cameraId: string): void {
    this.store.dispatch(spatialActions.listSpatialAnalysisRules({ cameraId }));
  }
}
